import React from "react";

import { StreamProvider, createSlotManager } from "@teamrota/shiftstreamlib";
import { COLORS } from "~/src/styles/config.style";
import {
  formatDateMonthTimeShort,
  formatHoursMinutes,
} from "~/src/utils/formatting";

export function cleanAddress(email: string) {
  return email.replace(/@ttmhealthcare\.(com|ie)/g, "@");
}

export function hasExternalSystem(provider: string) {
  return (
    provider !== StreamProvider.EMAIL && provider !== StreamProvider.INTERNAL
  );
}

export function getProviderLabel(provider: string) {
  switch (provider) {
    case StreamProvider.ALLOCATE:
    case StreamProvider.ALLOCATEV12:
      return "Allocate";
    case StreamProvider.SOFTRECRUIT:
      return "SoftRecruit";
    case StreamProvider.EMAIL:
      return "Email";
    case StreamProvider.INTERNAL:
      return "Internal";
    default:
      return provider;
  }
}

export function getFilterableProviders() {
  return [
    StreamProvider.ALLOCATEV12,
    StreamProvider.SOFTRECRUIT,
    StreamProvider.EMAIL,
    StreamProvider.INTERNAL,
  ];
}

export function getFillText(stream: any) {
  const slotManager = createSlotManager(stream.account, stream);

  const total = slotManager.totalCount();
  const filled = slotManager.filledCount();

  const percent = total > 0 ? Math.round((filled / total) * 100) : -1;

  const color =
    percent < 0
      ? COLORS.GREY_DARK
      : percent > 80
        ? COLORS.GREEN
        : percent > 0
          ? COLORS.MAIN_ORANGE
          : COLORS.RED;

  return (
    <span style={{ whiteSpace: "nowrap", color }}>
      {filled} of {total}
    </span>
  );
}

export function getTooltip(stream: any) {
  return (
    (stream.shift?.sourceAccount?.accountName ?? "Unknown") +
    "\n" +
    (stream.shift?.venue?.name ?? "Unknown") +
    "\n" +
    (stream.shift?.role?.name ?? "Unknown") +
    "\n" +
    (stream.shift?.startTime
      ? formatDateMonthTimeShort(stream.shift?.startTime)
      : "Unknown") +
    " to " +
    (stream.shift?.endTime
      ? formatHoursMinutes(stream.shift?.endTime)
      : "Unknown")
  );
}

/**
 * Nicer rendering of state + associated info
 */
export function getEmailStatus(email: any) {
  const noContact = email.contacts.length === 0;
  const noMapping =
    email.contacts.length > 0 &&
    email.contacts.every((c: any) => c.hasMapping === false);

  switch (email.state) {
    case "PENDING":
      return <>⌛️ Pending</>;

    case "ERROR":
      if (email.errorMessage) {
        return <div style={{ fontSize: "80%" }}>⛔️ {email.errorMessage}</div>;
      } else {
        return <>⛔️ Error</>;
      }

    case "SKIPPED":
      return (
        <>
          ⏭️ Skipped
          {email.replyTo && <div style={{ fontSize: "80%" }}>💬 Follow-Up</div>}
          {noContact && <div style={{ fontSize: "80%" }}>🤷 No Contact</div>}
          {noMapping && <div style={{ fontSize: "80%" }}>⚠️ No Mapping</div>}
        </>
      );

    case "PARTIAL":
      return (
        <>
          ⚠️ Partial
          {email.parseExplanation && (
            <div style={{ fontSize: "80%" }} title={email.parseExplanation}>
              {email.parseExplanation.length > 30
                ? `${email.parseExplanation.slice(0, 30)}...`
                : email.parseExplanation}
            </div>
          )}
        </>
      );

    case "COULD_NOT_PARSE": {
      return (
        <>
          ❌ Could Not Parse
          {email.parseMessage && (
            <div style={{ fontSize: "80%" }}>{email.parseMessage}</div>
          )}
        </>
      );
    }

    case "PARSED":
      if (email.isClarified) {
        return (
          <>
            🤔 Clarify{" "}
            <div style={{ fontSize: "80%" }}>
              {email.parsedClarify.join(", ")}
            </div>
          </>
        );
      } else {
        return <>✅ Parsed</>;
      }
  }

  return <>Unknown</>;
}

export function toHumanDuration(secondsAgo: number | null) {
  if (secondsAgo === null) {
    return "None";
  }

  if (secondsAgo < 60) {
    return `${secondsAgo}s ago`;
  } else if (secondsAgo < 3600) {
    const mins = Math.round(secondsAgo / 60);
    return `${mins}m ago`;
  } else if (secondsAgo < 3600 * 24) {
    const hours = Math.round(secondsAgo / 3600);
    return `${hours}h ago`;
  } else {
    const days = Math.round(secondsAgo / (3600 * 24));
    return `${days}d ago`;
  }
}

export function getStateColor(stream: any) {
  const isStarted = stream.state === "STARTED";
  const isMissed = stream.state === "MISSED";
  const isCancelled = stream.state === "CANCELLED";
  const isGone = stream.state === "GONE";
  const isReady = stream.state === "READY";

  const color = isReady
    ? COLORS.MAIN_ORANGE
    : isCancelled
      ? COLORS.RED
      : isMissed || isGone || isStarted
        ? COLORS.WARM_GREY
        : COLORS.MAIN_ORANGE;

  return color;
}
